import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { 
  BookOpen, 
  Users, 
  Zap, 
  Cpu, 
  Code, 
  Microscope,
  Star,
  ArrowRight,
  Target,
  Building2,
  GraduationCap,
  Church,
  Truck,
  MapPin,
  Calendar,
  Clock
} from 'lucide-react'

const HomePage = () => {
  const [workshopForm, setWorkshopForm] = useState({
    parentName: '',
    email: '',
    phone: '',
    childName: '',
    childAge: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleWorkshopSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      const { sendFormEmail } = await import('../utils/email')
      await sendFormEmail({
        source: 'Workshop Reservation',
        parentName: workshopForm.parentName,
        email: workshopForm.email,
        phone: workshopForm.phone,
        childName: workshopForm.childName,
        childAge: workshopForm.childAge,
      })
      setSubmitStatus('success')
      setWorkshopForm({ parentName: '', email: '', phone: '', childName: '', childAge: '' })
    } catch (error) {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleWorkshopChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkshopForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }
  const features = [
    {
      icon: Cpu,
      title: 'Tech Innovation Laboratory Maker Space',
      description: 'Where imagination meets innovation. Kids get hands-on with Robotics, Coding, AI, AR/VR, Drone Technology, Chip Design & more Electronics building real gadgets.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Code,
      title: 'Tech Entrepreneurship for Kids',
      description: 'From idea to invention and Product launching - nurturing future CEOs. Kids turn ideas into real products by brainstorming, prototyping, and launching their own creations.',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: Microscope,
      title: 'Do It Yourself Kits',
      description: 'Hands-on learning that continues beyond the makerspace. Each kit comes with step-by-step guides and all materials to build exciting Technology projects.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: Zap,
      title: 'State Level Competition League',
      description: 'Compete with peers and showcase your innovations. Monthly Industrial Expert Visits, School Partnerships, and Community Classes.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: Building2,
      title: 'Industry Expert Visits & Lab Tours',
      description: 'Monthly industry expert visits and kids also visit industrial technology labs. Real-world exposure to cutting-edge technology and professional environments.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      icon: GraduationCap,
      title: 'School Partnerships',
      description: 'Comprehensive STEM programs for schools. We bring our innovation lab experience directly to your educational institution with customized curriculum.',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50'
    },
    {
      icon: Church,
      title: 'Community Partnerships',
      description: 'For churches and other community organizations. We provide STEM education programs that bring families together through learning and innovation.',
      color: 'text-rose-600',
      bgColor: 'bg-rose-50'
    }
  ]

  const stats = [
    { number: '100+', label: 'Students' },
    { number: '15+', label: 'Expert Instructors' },
    { number: '70+', label: 'Projects' },
    { number: '4.9/5', label: 'Rating' }
  ]

  const testimonials = [
    {
      name: 'Sarah Johnson',
      age: 'Age 9',
      content: 'I built my first robot! StemBears made electronics so fun and easy to understand. The lab coat and safety glasses made me feel like a real engineer!',
      rating: 5,
      project: 'Marine Robot Builder'
    },
    {
      name: 'Alex Chen',
      age: 'Age 11',
      content: 'The entrepreneurship program is amazing! I turned my idea into a real product and launched it at the innovation fair.',
      rating: 5,
      project: 'Future CEO'
    },
    {
      name: 'Emma Rodriguez',
      age: 'Age 8',
      content: 'The DIY kits are perfect for home learning! I built a solar street light and my whole family was amazed.',
      rating: 5,
      project: 'Solar Engineer'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white pt-8 pb-12 lg:pt-12 lg:pb-16">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl lg:text-5xl font-bold mb-8 leading-tight tracking-wide text-left">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Future Engineers &<br />
                  Scientists
                </span>
                <br />
                <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                  are created here
                </span>
              </h1>
              <p className="text-lg lg:text-xl text-gray-700 mb-10 leading-relaxed max-w-2xl text-left">
                Innovation labs for the Next Generation. We turn afterschool programs into STEM Innovation laboratory maker space where kids build real-world solutions from robots, gadgets and AI powered apps to Semi Conductor Chip Designing with hands-on creativity and problem solving.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-start">
                <Link to="/programs" className="group bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold text-lg px-10 py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center">
                  Explore More
                  <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link to="/contact" className="group bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-semibold text-lg px-10 py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center">
                  <BookOpen className="mr-3 h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
                  Get Started Today
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10">
                <video
                  src="/videos/homepage-hero.mp4"
                  className="rounded-2xl shadow-2xl w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500 rounded-full animate-float"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-secondary-500 rounded-full animate-bounce-slow"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Pickup Banner */}
      <section className="bg-gradient-to-r from-green-500 via-green-600 to-emerald-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center justify-center gap-2 text-white"
          >
            <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
              <Truck className="w-4 h-4" />
            </div>
            <span className="font-medium text-xs sm:text-sm">
              Free Pickup Service up to 5 miles radius
            </span>
            <div className="hidden sm:flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">
              <MapPin className="w-3 h-3" />
              <span className="text-xs font-medium">Limited Time</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                {stat.label ? (
                  <>
                    <div className="text-4xl lg:text-5xl font-bold text-primary-600 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </>
                ) : (
                  <div className="text-2xl lg:text-3xl font-bold text-primary-600">
                    {stat.number}
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="relative py-24 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ef4444' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm20 0c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Icon and Title */}
            <div className="flex items-center justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-20"></div>
                <div className="relative bg-gradient-to-r from-red-500 to-orange-500 rounded-full p-4 shadow-2xl">
                  <Target className="w-8 h-8 text-white" />
                </div>
              </div>
              <h2 className="ml-6 text-4xl lg:text-5xl font-bold bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent pb-2">
                The Problem we're solving
              </h2>
            </div>

            {/* Main Content */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 lg:p-16 shadow-2xl border border-white/20">
              <p className="text-xl lg:text-2xl text-gray-800 leading-relaxed mb-8 font-medium">
                Many students don't get early exposure to STEM fields, missing opportunities to discover their potential in
                <span className="text-red-600 font-bold"> Science</span>, 
                <span className="text-blue-600 font-bold"> Technology</span>, 
                <span className="text-green-600 font-bold"> Engineering</span> and 
                <span className="text-purple-600 font-bold"> Mathematics</span>.
              </p>
              
              <div className="bg-gradient-to-r from-red-100 to-orange-100 rounded-2xl p-6 border-l-4 border-red-500">
                <p className="text-lg lg:text-xl text-gray-700 leading-relaxed font-semibold">
                  Without hands-on experience with these concepts, we lose countless future 
                  <span className="text-red-600"> Scientists</span>, 
                  <span className="text-blue-600"> Engineers</span>, and 
                  <span className="text-green-600"> creators</span> before they even know what's possible.
                </p>
              </div>
            </div>

            {/* Visual Elements */}
            <div className="mt-12 flex justify-center space-x-8 opacity-60">
              <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center animate-bounce">
                <span className="text-2xl">🔬</span>
              </div>
              <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center animate-bounce" style={{animationDelay: '0.2s'}}>
                <span className="text-2xl">💻</span>
              </div>
              <div className="w-16 h-16 bg-green-200 rounded-full flex items-center justify-center animate-bounce" style={{animationDelay: '0.4s'}}>
                <span className="text-2xl">⚙️</span>
              </div>
              <div className="w-16 h-16 bg-purple-200 rounded-full flex items-center justify-center animate-bounce" style={{animationDelay: '0.6s'}}>
                <span className="text-2xl">📐</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Free Electronics Workshop Announcement */}
      <section className="bg-gradient-to-r from-red-500 via-red-600 to-red-700 shadow-lg rounded-lg overflow-hidden mx-8 sm:mx-12 lg:mx-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <motion.div 
              className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 relative shadow-2xl"
              animate={{
                scale: [1, 1.02, 1, 1.02, 1],
                boxShadow: [
                  "0 10px 25px rgba(0,0,0,0.1)",
                  "0 15px 35px rgba(239, 68, 68, 0.3)",
                  "0 10px 25px rgba(0,0,0,0.1)",
                  "0 15px 35px rgba(239, 68, 68, 0.3)",
                  "0 10px 25px rgba(0,0,0,0.1)"
                ]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* FREE Stamp */}
              <motion.div
                className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-sm px-5 py-3 rounded-full shadow-xl border-2 border-white transform rotate-12"
                animate={{
                  rotate: [0, -5, 5, -5, 0],
                  scale: [1, 1.1, 1, 1.1, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                FREE
              </motion.div>
              <motion.h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
                animate={{
                  scale: [1, 1.05, 1, 1.05, 1]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                Free Electronics Workshop Coming Soon!
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-white">
                <div className="flex items-center justify-center gap-2">
                  <MapPin className="w-5 h-5 text-yellow-300" />
                  <span className="font-semibold">Location:</span>
                  <span>Collab Room, Hillsborough Recreation</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-300" />
                  <span className="font-semibold">Date:</span>
                  <span className="whitespace-nowrap">11/15/2025</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-5 h-5 text-pink-300" />
                  <span className="font-semibold">Time:</span>
                  <span className="whitespace-nowrap">11:00 AM - 12:00 PM</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Star className="w-5 h-5 text-purple-300" />
                  <span className="font-semibold">Fee:</span>
                  <span className="text-green-300 font-bold whitespace-nowrap">$0</span>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-center">
                <a
                  href="https://www.google.com/maps/dir/?api=1&origin&destination=Hillsborough%20Recreation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold px-5 py-2 rounded-lg transition-colors border border-white/20"
                >
                  <MapPin className="w-5 h-5" />
                  Get Directions
                </a>
              </div>
              <p className="text-white/90 text-lg mt-4">
                Join us for an exciting hands-on electronics workshop! Perfect for beginners and enthusiasts alike.
              </p>
              
              {/* Workshop Reservation Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-4 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 mx-2 sm:mx-4 shadow-xl"
              >
                <h3 className="text-lg font-semibold text-white mb-4 text-center">
                  Reserve Your Spot
                </h3>
                
                {submitStatus === 'success' ? (
                  <div className="text-center py-4">
                    <div className="text-green-300 text-lg font-semibold mb-2">✅ Spot Reserved!</div>
                    <p className="text-white/80 text-sm">We'll send you confirmation details soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleWorkshopSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        name="parentName"
                        placeholder="Parent Name"
                        value={workshopForm.parentName}
                        onChange={handleWorkshopChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={workshopForm.email}
                        onChange={handleWorkshopChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number"
                        value={workshopForm.phone}
                        onChange={handleWorkshopChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                      />
                      <input
                        type="text"
                        name="childName"
                        placeholder="Student Name"
                        value={workshopForm.childName}
                        onChange={handleWorkshopChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                      />
                    </div>
                    <input
                      type="text"
                      name="childAge"
                      placeholder="Student Grade"
                      value={workshopForm.childAge}
                      onChange={handleWorkshopChange}
                      required
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-4 px-8 rounded-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Reserving...
                        </>
                      ) : (
                        <>
                          <Star className="w-4 h-4" />
                          Reserve Free Spot
                        </>
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Makes <span className="bg-gradient-to-r from-red-700 to-red-600 bg-clip-text text-transparent">STEM BEARS</span> Special?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe every child has the potential to become a future tech innovator. Our STEM programs introduce kids to AI, Electronics, IOT & Robotics and Chip design, sparking curiosity through hands-on exploration. Students advance their skills through our three-level program Titans, Warriors, and Lakers which guides them step-by-step from basic electronics to advanced system thinking and chip design.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className={`w-20 h-20 ${feature.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`h-10 w-10 ${feature.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-center leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Building Curiosity & Confidence Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="bg-gradient-to-r from-red-700 to-red-600 bg-clip-text text-transparent animate-bounce">STEM BEARS</span> Programs?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Foundation for lifelong learning and innovation
            </p>
            <h3 className="text-2xl lg:text-3xl font-semibold text-gray-800 mb-6">
              Building Curiosity & Confidence
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-lg">01</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Creative Exploration</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Kids experiment with real-world electronics, coding, and AI projects that make learning exciting and build lasting confidence.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold text-lg">02</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Hands-On Challenges</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Every activity encourages problem-solving and teamwork, supported by easy-to-follow project guides and visual learning tools.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 font-bold text-lg">03</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">STEM Starter Kits</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Each learner receives a complete project kit that includes all essential components and no extra purchases needed to start creating.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-600 font-bold text-lg">04</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Guided by Experts</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our mentors are experienced educators and engineers who provide one-on-one guidance to help kids overcome challenges and grow their skills.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inspiring Growth & Real-World Connection Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Inspiring Growth & Real-World Connection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced opportunities for future innovators
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-600 font-bold text-lg">05</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Live Webinars & Career Insights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Students interact with industry professionals and innovators, gaining exposure to future tech careers through engaging sessions and demos.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-600 font-bold text-lg">06</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Research & Innovation Pathways</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Advanced learners get mentorship to explore research ideas and even prepare publications or presentations for STEM fairs and journals.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-pink-600 font-bold text-lg">07</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Showcase & Community Connection</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Regular exhibitions and STEM fair participation encourage kids to share their projects and celebrate creativity with their peers.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-600 font-bold text-lg">08</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Certificates & Recognition</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Each milestone is celebrated with progress certificates and feedback, inspiring young creators to keep learning and innovating.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Program Structure Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Structured Learning Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each level blends in-person sessions, hands-on projects, and celebration meet-ups that make learning accessible, engaging, and inspiring. Beyond our programs, we continue to mentor and connect with kids through workshops, newsletters, and interactive activities that bring STEM learning into everyday life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Level 1: Cosmos Juniors */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 bg-cyan-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Cpu className="h-8 w-8 text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Level 1: Cosmos Juniors</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Basic introduction of Technology through playful, creative, hands-on activities.
              </p>
              <div className="text-center">
                <span className="inline-block bg-cyan-100 text-cyan-800 px-4 py-2 rounded-full text-sm font-medium">
                  Ages 6-8
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Cpu className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Level 2: Titans</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Ignite curiosity through hands-on sessions exploring AI, 3D Design, Web Development & Programming. Perfect introduction to STEM for young innovators.
              </p>
              <div className="text-center">
                <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                  Ages 9-15
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Code className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Level 3: Warriors</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Advance curiosity with Programming, Electronics, Robotics, IoT, and database projects. Build real-world skills through immersive programs that foster creativity and innovation.
              </p>
              <div className="text-center">
                <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                  Ages 9-15
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Microscope className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Level 4: Lakers</h3>
              <p className="text-gray-600 text-center leading-relaxed mb-6">
                Engage in hands-on Data science, chip design, and AR/VR development and Drone Technology. Gain mastery in advanced technologies, empowering future innovators.
              </p>
              <div className="text-center">
                <span className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium">
                  Ages 9-15
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Kids & Parents Say
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from our amazing community of young learners
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic text-lg leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="border-t border-gray-100 pt-6">
                  <div className="font-bold text-gray-900 text-lg">{testimonial.name}</div>
                  <div className="text-gray-500 mb-2">{testimonial.age}</div>
                  <div className="text-primary-600 font-semibold">
                    {testimonial.project}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              <span className="block">Grand Opening Offer and Year End</span>
              <span className="block">Festival Season Offer!</span>
            </h2>
            <div className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-10 max-w-4xl mx-auto space-y-2">
              <p className="font-semibold">Limited Spots Only - Save 15% Before It's Gone!</p>
              <p>Offer ends on February 28, 2026</p>
              <p className="text-sm sm:text-base">Additional 10% Discount for Sibling</p>
              <p className="text-sm sm:text-base">GET BASKETBALL MATCH TICKETS FOR FREE FOR THE FIRST 10 REGISTERED STUDENTS!</p>
              
            </div>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Link 
                to="/register" 
                state={{ programName: 'STEM Bears Programs' }} 
                className="group bg-white text-blue-600 hover:bg-gray-50 font-bold text-lg px-8 sm:px-10 py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center min-w-[200px] border border-purple-200"
              >
                Pre Register Today
                <BookOpen className="ml-3 h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
              </Link>
              <Link 
                to="/contact" 
                className="group border-2 border-white text-white hover:bg-white hover:text-blue-600 font-bold text-lg px-8 sm:px-10 py-4 rounded-2xl transition-all duration-300 flex items-center justify-center min-w-[200px]"
              >
                Get in Touch
                <Users className="ml-3 h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
              </Link>
            </div>

            
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
